import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { DeleteModal } from '../../../Common/DeleteModal';
import { listWeeklyReports, WeeklyReportStatus } from '../../../api/weeklyReport/listWeeklyReports';
import { deleteWeeklyReport } from '../../../api/weeklyReport/deleteWeeklyReport';
import './WeeklyReportsTable.css';
import Select from 'react-select';
import ShimmerLoader from '../../../Common/ShimmerLoader';

// Time-based status logic using IST (UTC+5:30)
const getStatus = (status: string) => {
  if (!status) return 'Draft';
  if (status === 'PUBLISHED') return 'Live';
  if (status === 'YET_TO_PUBLISH') return 'Yet to Publish';
  return 'Draft';
};

// Helper to format date as yyyy-MM-dd in IST
function formatDateToIST(dateStr: string) {
  if (!dateStr) return '-';
  // If already a full ISO string, use as is. If just a date, add T00:00:00.
  let dateObj;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    dateObj = new Date(dateStr + 'T00:00:00');
  } else {
    dateObj = new Date(dateStr);
  }
  if (isNaN(dateObj.getTime())) return '-';
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const WeeklyReportsTable = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const actionButtonRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({});
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string, title: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<WeeklyReportStatus>('ALL');
  const [search, setSearch] = useState('');
  const [filteredReports, setFilteredReports] = useState<any[]>([]);

  useEffect(() => {
    fetchReports(page, pageSize, statusFilter);
    // eslint-disable-next-line
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    // Filter reports by search (title or tag)
    if (!search.trim()) {
      setFilteredReports(reports);
    } else {
      const lower = search.trim().toLowerCase();
      setFilteredReports(
        reports.filter(
          (r) =>
            (r.title && r.title.toLowerCase().includes(lower)) ||
            (r.tag && r.tag.toLowerCase().includes(lower)) ||
            (r.tournament && r.tournament.name && r.tournament.name.toLowerCase().includes(lower))
        )
      );
    }
  }, [search, reports]);

  const fetchReports = async (page: number, pageSize: number, status: any) => {
    try {
      setLoading(true);
      const data = await listWeeklyReports(page, pageSize, status);
      setReports(Array.isArray(data.paginatedResults) ? data.paginatedResults : []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (openDropdown) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenDropdown(null);
          setDropdownPosition(null);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const handleView = (report: any) => {
    navigate('/WeeklyReports/detail', { state: { edit: true, id: report._id, report, title: report.title } });
  };

  const handleEdit = (report: any) => {
    navigate('/WeeklyReports/detail', { state: { edit: true, id: report._id, report, title: report.title } });
  };

  const handleDelete = (id: string, title: string) => {
    setDeleteTarget({ id, title });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteTarget) {
      try {
        await deleteWeeklyReport(deleteTarget.id);
        await fetchReports(page, pageSize, statusFilter); // Refresh the list after deletion
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } catch (error) {
        console.error('Error deleting report:', error);
      }
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleDropdown = (id: string) => {
    if (openDropdown === id) {
      setOpenDropdown(null);
      setDropdownPosition(null);
    } else {
      const buttonRef = actionButtonRefs.current[id];
      if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        setDropdownPosition({
          x: rect.right - 120, // Position dropdown to the left of the button
          y: rect.bottom + 4   // Position dropdown below the button
        });
      }
      setOpenDropdown(id);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1); // Reset to first page on page size change
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value as WeeklyReportStatus);
    setPage(1); // Reset to first page when filter changes
  };

  if (loading) {
    return (
      <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
        <div className="filter-row">
          <div className="filter-controls">
            <div style={{ width: '160px', height: '38px', marginRight: 12 }} className="shimmer-base" />
            <div style={{ width: '240px', height: '38px' }} className="shimmer-base" />
          </div>
        </div>
        <ShimmerLoader type="table-rows" rows={5} />
      </div>
    );
  }

  return (
    <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
      <div className="filter-row">
        <div className="filter-controls">
          <Select
            classNamePrefix="react-select"
            value={{ value: statusFilter, label: statusFilter === 'ALL' ? 'All' : statusFilter === 'PUBLISHED' ? 'Live' : 'Yet to Publish' }}
            onChange={option => {
              if (option) {
                setStatusFilter(option.value as WeeklyReportStatus);
                setPage(1);
              }
            }}
            options={[
              { value: 'ALL', label: 'All' },
              { value: 'PUBLISHED', label: 'Live' },
              { value: 'YET_TO_PUBLISH', label: 'Yet to Publish' }
            ]}
            placeholder="Status"
            styles={{
              control: (base) => ({ ...base, minHeight: '38px', width: '160px', marginRight: 12 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
            isSearchable={false}
          />
          <input
            type="text"
            className="form-search"
            placeholder="Search by title, tag, or tournament"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 240 }}
          />
        </div>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Title</th>
            <th>Tournaments/Tags</th>
            <th>Publish Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredReports.length === 0 ? (
            <tr><td colSpan={6} className="text-center">No reports found</td></tr>
          ) : (
            filteredReports.map((report, idx) => (
              <tr
                key={report._id || idx}
                className="clickable-row"
                onClick={() => handleView(report)}
                style={{ cursor: 'pointer' }}
              >
                <td>{(page - 1) * pageSize + idx + 1}</td>
                <td>{report.title}</td>
                <td>{report.tournament?.name || report.tag || '-'}</td>
                <td>{formatDateToIST(report.dateUploaded)}</td>
                <td>
                  <span className={`status-badge ${getStatus(report.status) === 'Live' ? 'live' : getStatus(report.status) === 'Yet to Publish' ? 'inactive' : 'draft'}`}>
                    {getStatus(report.status)}
                  </span>
                </td>
                <td style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                  <span
                    className="actions-icon"
                    title="Actions"
                    onClick={e => {
                      e.stopPropagation();
                      handleDropdown(report._id);
                    }}
                    ref={el => (actionButtonRefs.current[report._id] = el)}
                  >
                    &#8942;
                  </span>
                  {openDropdown === report._id && dropdownPosition && (
                    createPortal(
                      <div
                        className="dropdown-menu-actions"
                        ref={el => (dropdownRefs.current[report._id] = el)}
                        onClick={e => e.stopPropagation()}
                        style={{
                          position: 'fixed',
                          top: dropdownPosition.y,
                          left: dropdownPosition.x,
                          zIndex: 9999,
                        }}
                      >
                        <div className="dropdown-item" onClick={() => { handleEdit(report); setOpenDropdown(null); setDropdownPosition(null); }}>Edit</div>
                        <div className="dropdown-item delete" onClick={() => { handleDelete(report._id, report.title); setOpenDropdown(null); setDropdownPosition(null); }}>Delete</div>
                      </div>,
                      document.body
                    )
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {/* Pagination UI */}
      <div
        className="d-flex pagination-row align-items-center mt-3"
        style={{
          width: 1160,
          height: 52,
          justifyContent: 'space-between',
          borderRadius: '8px', // 'sm' mapped to 8px
          paddingTop: 8,
          paddingRight: 12,
          paddingBottom: 8,
          paddingLeft: 12,
        }}
      >
        <div
          style={{
            fontFamily: 'Helvetica Neue, Arial, sans-serif',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: 0,
          }}
        >
          Showing results
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="mx-2"
            style={{
              width: 70,
              height: 36,
              borderRadius: 8, // 'sm' mapped to 8px
              padding: 8,
              gap: 8,
              borderWidth: 0.5,
              borderStyle: 'solid',
              borderColor: '#888', // or your preferred border color
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontSize: '12px',
            }}
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          of {totalCount}
        </div>
        <div className="d-flex align-items-center">
          Page
          <button className="btn btn-light mx-1" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>&lt;</button>
          <span className="btn btn-primary mx-1 disabled">{page}</span>
          {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1 + page).map(p => (
            p <= totalPages ? (
              <button key={p} className="btn btn-light mx-1" onClick={() => handlePageChange(p)}>{p}</button>
            ) : null
          ))}
          {page < totalPages - 2 && <span className="mx-1">...</span>}
          <button className="btn btn-light mx-1" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>&gt;</button>
          <span className="mx-2">of {totalPages}</span>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDelete}
        title="Are you sure you want to delete this weekly report?"
        message={deleteTarget ? `Are you sure you want to delete the weekly report with the title: "${deleteTarget.title}"?` : ''}
        confirmLabel="Yes"
        cancelLabel="No"
      />
    </div>
  );
};

export default WeeklyReportsTable; 